import * as React from 'react';
import { AlertCircle, Eye, EyeOff, Check } from 'lucide-react';

// ============================================
// Input Component
// ============================================

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
}

/**
 * Standardized Input Component
 * 
 * Design System Specifications:
 * - Border: Light gray (#e9e9e9)
 * - Focus: Blue border (#0084c7)
 * - Error: Red border (#dc2626)
 * - Height: 36px (sm), 44px (md), 48px (lg)
 * 
 * Usage:
 * ```tsx
 * <Input 
 *   label="Email" 
 *   placeholder="Enter your email"
 *   error="Invalid email address"
 * />
 * ```
 */
export function Input({
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  fullWidth = true,
  inputSize = 'md',
  className = '',
  ...props
}: InputProps) {
  const sizes = {
    sm: 'h-[36px] px-3 text-[13px]',
    md: 'h-[44px] px-4 text-[14px]',
    lg: 'h-[48px] px-4 text-[16px]',
  };

  const hasError = !!error;
  const borderColor = hasError 
    ? 'border-[#dc2626] focus:border-[#dc2626] focus:ring-[#dc2626]/20' 
    : 'border-[#e9e9e9] focus:border-[#0084c7] focus:ring-[#0084c7]/20';

  const widthClass = fullWidth ? 'w-full' : '';
  const paddingLeft = leftIcon ? 'pl-10' : '';
  const paddingRight = rightIcon ? 'pr-10' : '';

  return (
    <div className={`${widthClass}`}>
      {label && (
        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b6b6b]">
            {leftIcon}
          </div>
        )}
        <input
          className={`
            ${sizes[inputSize]}
            ${borderColor}
            ${paddingLeft}
            ${paddingRight}
            ${widthClass}
            ${className}
            bg-white
            border
            rounded-[10px]
            text-[#1c1c1c]
            placeholder:text-[#9ca3af]
            focus:outline-none
            focus:ring-2
            disabled:bg-[#f5f5f5]
            disabled:cursor-not-allowed
            disabled:text-[#6b6b6b]
            transition-colors
          `.trim()}
          {...props}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b6b6b]">
            {rightIcon}
          </div>
        )}
        {error && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#dc2626]">
            <AlertCircle className="size-4" />
          </div>
        )}
      </div>
      {hint && !error && (
        <p className="text-[#6b6b6b] text-[13px] mt-1">{hint}</p>
      )}
      {error && (
        <p className="text-[#dc2626] text-[13px] mt-1 flex items-center gap-1">
          {error}
        </p>
      )}
    </div>
  );
}

// ============================================
// Password Input Component
// ============================================

export interface PasswordInputProps extends Omit<InputProps, 'type' | 'rightIcon'> {}

export function PasswordInput(props: PasswordInputProps) {
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <Input
      {...props}
      type={showPassword ? 'text' : 'password'}
      rightIcon={
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="p-1 hover:bg-[#f5f5f5] rounded transition-colors"
        >
          {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      }
    />
  );
}

// ============================================
// Textarea Component
// ============================================

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  fullWidth?: boolean;
  resize?: boolean;
}

export function Textarea({
  label,
  error,
  hint,
  fullWidth = true,
  resize = true,
  className = '',
  ...props
}: TextareaProps) {
  const hasError = !!error;
  const borderColor = hasError 
    ? 'border-[#dc2626] focus:border-[#dc2626] focus:ring-[#dc2626]/20' 
    : 'border-[#e9e9e9] focus:border-[#0084c7] focus:ring-[#0084c7]/20';

  const widthClass = fullWidth ? 'w-full' : '';
  const resizeClass = resize ? 'resize-y' : 'resize-none';

  return (
    <div className={widthClass}>
      {label && (
        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
          {label}
        </label>
      )}
      <textarea
        className={`
          ${borderColor}
          ${widthClass}
          ${resizeClass}
          ${className}
          min-h-[100px]
          px-4
          py-3
          bg-white
          border
          rounded-[10px]
          text-[#1c1c1c]
          text-[14px]
          placeholder:text-[#9ca3af]
          focus:outline-none
          focus:ring-2
          disabled:bg-[#f5f5f5]
          disabled:cursor-not-allowed
          disabled:text-[#6b6b6b]
          transition-colors
        `.trim()}
        {...props}
      />
      {hint && !error && (
        <p className="text-[#6b6b6b] text-[13px] mt-1">{hint}</p>
      )}
      {error && (
        <p className="text-[#dc2626] text-[13px] mt-1 flex items-center gap-1">
          <AlertCircle className="size-3" />
          {error}
        </p>
      )}
    </div>
  );
}

// ============================================
// Select Component
// ============================================

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
  fullWidth?: boolean;
  selectSize?: 'sm' | 'md' | 'lg';
}

export function Select({
  label,
  error,
  hint,
  options,
  placeholder,
  fullWidth = true,
  selectSize = 'md',
  className = '',
  ...props
}: SelectProps) {
  const sizes = {
    sm: 'h-[36px] px-3 text-[13px]',
    md: 'h-[44px] px-4 text-[14px]',
    lg: 'h-[48px] px-4 text-[16px]',
  };

  const hasError = !!error;
  const borderColor = hasError 
    ? 'border-[#dc2626] focus:border-[#dc2626] focus:ring-[#dc2626]/20' 
    : 'border-[#e9e9e9] focus:border-[#0084c7] focus:ring-[#0084c7]/20';

  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <div className={widthClass}>
      {label && (
        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
          {label}
        </label>
      )}
      <select
        className={`
          ${sizes[selectSize]}
          ${borderColor}
          ${widthClass}
          ${className}
          bg-white
          border
          rounded-[10px]
          text-[#1c1c1c]
          focus:outline-none
          focus:ring-2
          disabled:bg-[#f5f5f5]
          disabled:cursor-not-allowed
          disabled:text-[#6b6b6b]
          transition-colors
          appearance-none
          bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xIDFMNiA2TDExIDEiIHN0cm9rZT0iIzZiNmI2YiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cg==')]
          bg-[position:right_12px_center]
          bg-no-repeat
          pr-10
        `.trim()}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option 
            key={option.value} 
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      {hint && !error && (
        <p className="text-[#6b6b6b] text-[13px] mt-1">{hint}</p>
      )}
      {error && (
        <p className="text-[#dc2626] text-[13px] mt-1 flex items-center gap-1">
          <AlertCircle className="size-3" />
          {error}
        </p>
      )}
    </div>
  );
}

// ============================================
// Checkbox Component
// ============================================

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
}

export function Checkbox({
  label,
  error,
  className = '',
  ...props
}: CheckboxProps) {
  const hasError = !!error;
  
  return (
    <div>
      <label className="flex items-start gap-3 cursor-pointer group">
        <div className="relative flex items-center justify-center shrink-0 mt-0.5">
          <input
            type="checkbox"
            className={`
              size-5
              rounded-[6px]
              border
              ${hasError ? 'border-[#dc2626]' : 'border-[#e9e9e9]'}
              text-[#0084c7]
              focus:ring-2
              focus:ring-[#0084c7]/20
              focus:ring-offset-0
              disabled:cursor-not-allowed
              disabled:opacity-50
              transition-colors
              cursor-pointer
              ${className}
            `.trim()}
            {...props}
          />
        </div>
        {label && (
          <span className="text-[#1c1c1c] text-[14px] select-none">
            {label}
          </span>
        )}
      </label>
      {error && (
        <p className="text-[#dc2626] text-[13px] mt-1 ml-8 flex items-center gap-1">
          <AlertCircle className="size-3" />
          {error}
        </p>
      )}
    </div>
  );
}

// ============================================
// Radio Group Component
// ============================================

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  error?: string;
  orientation?: 'vertical' | 'horizontal';
}

export function RadioGroup({
  name,
  options,
  value,
  onChange,
  label,
  error,
  orientation = 'vertical',
}: RadioGroupProps) {
  const layoutClass = orientation === 'horizontal' 
    ? 'flex flex-wrap gap-4' 
    : 'space-y-3';

  return (
    <div>
      {label && (
        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-3">
          {label}
        </label>
      )}
      <div className={layoutClass}>
        {options.map((option) => (
          <label 
            key={option.value}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange?.(e.target.value)}
              disabled={option.disabled}
              className="size-5 border-[#e9e9e9] text-[#0084c7] focus:ring-2 focus:ring-[#0084c7]/20 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            />
            <span className="text-[#1c1c1c] text-[14px] select-none">
              {option.label}
            </span>
          </label>
        ))}
      </div>
      {error && (
        <p className="text-[#dc2626] text-[13px] mt-2 flex items-center gap-1">
          <AlertCircle className="size-3" />
          {error}
        </p>
      )}
    </div>
  );
}

// ============================================
// Form Group Component
// ============================================

export interface FormGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: 1 | 2 | 3 | 4;
}

export function FormGroup({ 
  columns = 1, 
  children, 
  className = '',
  ...props 
}: FormGroupProps) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div 
      className={`grid ${gridCols[columns]} gap-4 md:gap-6 ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
}
